/**
 * Generic envelope wrapping responses from the live KSS BillBook API.
 *
 * Most endpoints return this shape (verified against the swagger
 * `ObjectResponse` schema):
 *
 *   { success: true,  message?: string, data: T   }    // happy path
 *   { success: false, message: "...",   data: null }   // server-handled error
 *
 * **But a small number of endpoints — notably `/api/Auth/login`,
 * `/api/Auth/refresh-token`, `/api/Auth/verify-otp` — return a FLAT
 * payload** (no envelope, just the data fields at the top level):
 *
 *   { accessToken, refreshToken, sessionId, expiresAt, user, tenant, ... }
 *
 * `unwrap()` accepts both shapes:
 *   - If the body looks enveloped (has `success` boolean + `data` key),
 *     it strips the envelope per the schema.
 *   - If the body is flat, it returns the body unchanged.
 *
 * This used to be the source of a "Empty response payload" error on
 * login when the strict envelope check fired on a flat AuthResponse
 * body. Fixed 2026-05-15 — now login succeeds end-to-end as long as
 * the body is non-empty.
 */
export interface ObjectResponse<T = unknown> {
  success: boolean;
  message?: string | null;
  data: T | null;
}

export interface UnwrapOptions {
  /**
   * When true, a `data: null` body is treated as success and returns
   * `undefined` instead of throwing. Use for endpoints that return no
   * meaningful payload — `delete`, `revoke`, etc.
   */
  allowNullData?: boolean;
}

/**
 * Type-guard: does this look like an `ObjectResponse` envelope?
 *
 * Heuristic: the body is a plain object with a boolean `success`
 * field. Successful envelopes carry `data`; failure envelopes often
 * omit `data` (just `{ success: false, message: "..." }`) so we
 * don't require it. Arrays, strings, and flat objects without
 * `success` are treated as "already unwrapped" and returned as-is.
 */
function isEnveloped<T>(body: unknown): body is ObjectResponse<T> {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  const o = body as Record<string, unknown>;
  return typeof o.success === 'boolean';
}

/**
 * Strip the `ObjectResponse` envelope when present; pass flat bodies
 * through unchanged. Throws on enveloped failures (`success: false`)
 * or on truly empty payloads.
 */
export function unwrap<T>(body: ObjectResponse<T> | T | undefined | null, opts: UnwrapOptions = {}): T {
  if (body === null || body === undefined) {
    if (opts.allowNullData) return undefined as unknown as T;
    throw new Error('Empty response payload');
  }

  // Path A — flat (envelope-less) response. Common for auth endpoints
  // and anything the backend forgot to wrap. Return as-is.
  if (!isEnveloped<T>(body)) {
    return body as T;
  }

  // Path B — proper envelope. Apply the original strict rules.
  const env = body as ObjectResponse<T>;
  if (env.success === false) {
    throw new Error(env.message || 'Request failed');
  }
  if (env.data == null) {
    if (opts.allowNullData) return undefined as unknown as T;
    throw new Error(env.message || 'Empty response payload');
  }
  return env.data;
}

/**
 * Defensive list-unwrap. The swagger spec doesn't pin whether entity
 * collection endpoints respond with `data: T[]` or `data: { items: T[] }`
 * or a bare top-level array, so this helper accepts every shape and
 * always returns a plain array.
 *
 * If the body is neither shape (e.g. an envelope-y object without
 * `items`), returns an empty array rather than throwing — list pages
 * handle empty gracefully and a noisy throw here would block the
 * whole grid render.
 */
export function unwrapItems<T>(
  body: ObjectResponse<T[] | { items?: T[] | null }> | T[] | { items?: T[] } | undefined | null,
): T[] {
  if (body == null) return [];

  // Bare top-level array (some endpoints return `[]` directly)
  if (Array.isArray(body)) return body as T[];

  // Top-level `{ items: [...] }` (also common — flat pagination shape)
  if (typeof body === 'object' && Array.isArray((body as { items?: T[] }).items)) {
    return (body as { items: T[] }).items;
  }

  // Standard envelope
  if (isEnveloped<unknown>(body)) {
    const env = body as ObjectResponse<unknown>;
    if (env.success === false) throw new Error(env.message || 'Request failed');
    const d = env.data;
    if (Array.isArray(d)) return d as T[];
    if (d && typeof d === 'object' && Array.isArray((d as { items?: T[] }).items)) {
      return (d as { items: T[] }).items;
    }
  }
  return [];
}
